import {
  CODIGOS_DE_FALHA_DA_ANALISE,
  ROTULO_DA_DECISAO,
  ROTULO_DA_FAIXA_DO_BUREAU,
  ROTULO_DA_FALHA_DA_ANALISE,
  ROTULO_DA_SITUACAO_DA_CNH,
  ROTULO_DA_VERIFICACAO_DA_ANALISE,
  ROTULO_DO_RESULTADO_DA_VERIFICACAO,
  ROTULO_DO_STATUS_DA_ANALISE,
  VERIFICACOES_DA_ANALISE,
  type AnaliseCadastral,
  type DecisaoDaAnalise,
  type ResultadoDaVerificacaoDaAnalise,
} from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import {
  Hourglass,
  LoaderCircle,
  ScanFace,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import type { ReactElement } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora } from '@/lib/datas'
import { usePermissao } from '@/lib/permissoes'

import { useIntegracaoMiraScore, useSolicitarAnalise } from './api'

/**
 * O QUADRO DA ANÁLISE CADASTRAL (Mira Score) — na fila e na reserva.
 *
 * ===========================================================================
 * A RECOMENDAÇÃO NUNCA VEM SÓ COMO COR
 * ===========================================================================
 * "Favorável / Atenção / Desfavorável" é sempre texto + ícone + cor (regra do
 * CLAUDE.md). E "Atenção" é o âmbar de `--warning`, nunca o amarelo da marca —
 * aqui amarelo é a cor da Locaveri, e uma recomendação pintada com ela leria
 * como destaque, não como alerta.
 *
 * ===========================================================================
 * A ANÁLISE INFORMA; QUEM DECIDE É O GESTOR
 * ===========================================================================
 * O quadro não tem botão de aprovar — os botões continuam os da fila. E toda
 * fonte que o Mira não consultou aparece como "não consultado": "favorável"
 * com metade das fontes fora não é favorável.
 */

const DECISAO: Readonly<
  Record<DecisaoDaAnalise, { variante: 'success' | 'warning' | 'danger'; icone: LucideIcon }>
> = {
  VERDE: { variante: 'success', icone: ShieldCheck },
  AMARELO: { variante: 'warning', icone: TriangleAlert },
  VERMELHO: { variante: 'danger', icone: ShieldAlert },
}

const VARIANTE_DA_VERIFICACAO: Readonly<
  Record<ResultadoDaVerificacaoDaAnalise, 'success' | 'warning' | 'danger' | 'outline'>
> = {
  APROVADO: 'success',
  ALERTA: 'warning',
  REPROVADO: 'danger',
  NAO_CONSULTADO: 'outline',
  INDISPONIVEL: 'outline',
}

const GRAVIDADE = {
  ALTA: 'danger',
  MEDIA: 'warning',
  BAIXA: 'neutral',
} as const

export function QuadroDaAnalise({
  analise,
  reservaId,
}: {
  readonly analise: AnaliseCadastral | null
  readonly reservaId: string
}): ReactElement | null {
  const integracao = useIntegracaoMiraScore()
  const podePedir = usePermissao('credit_analysis.create')

  // Integração desligada no ambiente (ou papel sem leitura): o quadro some e a
  // fila fica como sempre foi.
  if (integracao.data === undefined || integracao.data.modo === 'DESLIGADO') {
    return null
  }

  const vencida =
    analise?.status === 'CONCLUIDA' &&
    analise.validaAte !== null &&
    new Date(analise.validaAte).getTime() < Date.now()
  const podePedirDeNovo =
    analise === null ||
    vencida ||
    ['CONSENTIMENTO_RECUSADO', 'CONSENTIMENTO_EXPIRADO', 'CANCELADA', 'FALHOU'].includes(
      analise.status,
    )

  return (
    <section
      aria-label="Análise cadastral do Mira Score"
      className="space-y-3 rounded-md border border-border p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-fg-muted">
          Análise cadastral · Mira Score
          {integracao.data.modo === 'FAKE' ? ' (simulado)' : ''}
        </p>
        {analise?.origem === 'MANUAL' ? <Badge variant="neutral">Pedida à mão</Badge> : null}
      </div>

      <Situacao analise={analise} />

      {analise === null && !integracao.data.configurada ? (
        <p className="text-xs text-fg-secondary">
          A locadora ainda não configurou a chave do Mira Score.{' '}
          <Link to="/configuracoes/mira-score" className="underline underline-offset-4">
            Configurar agora
          </Link>
        </p>
      ) : null}

      {podePedir && podePedirDeNovo && integracao.data.configurada ? (
        <BotaoDePedir reservaId={reservaId} deNovo={analise !== null} />
      ) : null}

      <p className="text-xs text-fg-muted">A análise informa; quem decide é você.</p>
    </section>
  )
}

function Situacao({ analise }: { readonly analise: AnaliseCadastral | null }): ReactElement {
  if (analise === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-fg-secondary">
        <ShieldQuestion className="size-4 shrink-0" aria-hidden="true" />
        Este cliente ainda não foi analisado.
      </p>
    )
  }

  const rotuloDoStatus = ROTULO_DO_STATUS_DA_ANALISE[analise.status]

  switch (analise.status) {
    case 'SOLICITANDO':
    case 'EM_ANALISE':
      return (
        <p className="flex items-center gap-2 text-sm text-fg-secondary">
          <LoaderCircle className="size-4 shrink-0 animate-spin" aria-hidden="true" />
          {rotuloDoStatus}…
        </p>
      )
    case 'AGUARDANDO_CONSENTIMENTO':
      return (
        <div className="space-y-1 text-sm">
          <Badge variant="info">
            <ScanFace aria-hidden="true" />
            {rotuloDoStatus}
          </Badge>
          <p className="text-xs text-fg-secondary">
            O cliente recebeu o pedido no app e por e-mail: aceitar o termo e tirar uma selfie.
            {analise.consentimentoExpiraEm === null
              ? ''
              : ` Expira em ${formatarDataHora(analise.consentimentoExpiraEm)}.`}
          </p>
        </div>
      )
    case 'CONCLUIDA':
      return analise.resultado === null ? (
        <p className="text-sm text-fg-secondary">{rotuloDoStatus}</p>
      ) : (
        <Resultado analise={analise} resultado={analise.resultado} />
      )
    case 'CONSENTIMENTO_RECUSADO':
    case 'CONSENTIMENTO_EXPIRADO':
      return (
        <div className="space-y-1 text-sm">
          <Badge variant="warning">
            <Hourglass aria-hidden="true" />
            {rotuloDoStatus}
          </Badge>
          <p className="text-xs text-fg-secondary">
            Você pode decidir sem a análise, ou pedir de novo — o cliente recebe outro pedido.
          </p>
        </div>
      )
    case 'FALHOU':
      return (
        <div className="space-y-1 text-sm">
          <Badge variant="danger">
            <TriangleAlert aria-hidden="true" />
            {rotuloDoStatus}
          </Badge>
          {/* O texto da falha sai do dicionário quando o Mira deu o código —
              a mensagem dele é diagnóstico, o rótulo diz o que FAZER. */}
          {mensagemDaFalha(analise) === null ? null : (
            <p className="text-xs text-fg-secondary">{mensagemDaFalha(analise)}</p>
          )}
        </div>
      )
    case 'CANCELADA':
      return <Badge variant="neutral">{rotuloDoStatus}</Badge>
  }
}

/** O que o gestor lê numa análise que FALHOU: o rótulo do código, senão a mensagem. */
function mensagemDaFalha(analise: AnaliseCadastral): string | null {
  const codigo = CODIGOS_DE_FALHA_DA_ANALISE.find((item) => item === analise.codigoDoErro)
  return codigo === undefined ? analise.erro : ROTULO_DA_FALHA_DA_ANALISE[codigo]
}

function Resultado({
  analise,
  resultado,
}: {
  readonly analise: AnaliseCadastral
  readonly resultado: NonNullable<AnaliseCadastral['resultado']>
}): ReactElement {
  const { variante, icone: Icone } = DECISAO[resultado.decisao]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant={variante} className="px-2.5 py-1 text-sm">
          <Icone aria-hidden="true" />
          Recomendação: {ROTULO_DA_DECISAO[resultado.decisao]}
        </Badge>
        {/* A FAIXA da nota do birô — o Mira nunca mostra o número a
            terceiros. Informa, não pinta: a recomendação e os motivos é que
            contam. */}
        {resultado.faixaDoBureau === null ? null : (
          <span className="text-sm">
            Nota de crédito:{' '}
            <span className="font-semibold">
              {ROTULO_DA_FAIXA_DO_BUREAU[resultado.faixaDoBureau]}
            </span>
          </span>
        )}
        {resultado.score === null ? null : (
          <span className="text-sm">
            Nota <span className="font-semibold tabular-nums">{resultado.score}</span>
            <span className="text-fg-secondary"> de 1000 (maior é melhor)</span>
          </span>
        )}
      </div>

      {resultado.motivos.length === 0 ? null : (
        <ul className="space-y-1">
          {resultado.motivos.map((motivo) => (
            <li
              key={`${motivo.codigo}-${motivo.descricao}`}
              className="flex items-start gap-2 text-sm"
            >
              <Badge variant={GRAVIDADE[motivo.gravidade]} className="mt-0.5">
                {motivo.gravidade === 'ALTA'
                  ? 'Grave'
                  : motivo.gravidade === 'MEDIA'
                    ? 'Médio'
                    : 'Leve'}
              </Badge>
              <span>{motivo.descricao}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        {VERIFICACOES_DA_ANALISE.map((chave) => {
          const verificacao = resultado.verificacoes[chave]
          return (
            <Badge
              key={chave}
              variant={VARIANTE_DA_VERIFICACAO[verificacao.resultado]}
              title={verificacao.detalhe ?? undefined}
            >
              {ROTULO_DA_VERIFICACAO_DA_ANALISE[chave]}:{' '}
              {ROTULO_DO_RESULTADO_DA_VERIFICACAO[verificacao.resultado].toLowerCase()}
            </Badge>
          )
        })}
      </div>

      <p className="text-xs text-fg-secondary">
        {resultado.cnh.situacao === null
          ? 'CNH não encontrada na consulta.'
          : `CNH ${ROTULO_DA_SITUACAO_DA_CNH[resultado.cnh.situacao]}${
              resultado.cnh.validade === null
                ? ''
                : `, válida até ${formatarData(resultado.cnh.validade)}`
            }${resultado.cnh.categoria === null ? '' : ` (categoria ${resultado.cnh.categoria})`}.`}{' '}
        Analisado em {formatarData(resultado.concluidaEm)}
        {analise.validaAte === null ? '' : ` · vale até ${formatarData(analise.validaAte)}`}.
      </p>
    </div>
  )
}

function BotaoDePedir({
  reservaId,
  deNovo,
}: {
  readonly reservaId: string
  readonly deNovo: boolean
}): ReactElement {
  const pedir = useSolicitarAnalise(reservaId)
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={pedir.isPending}
      onClick={() => {
        pedir.mutate(undefined, {
          onSuccess: () => {
            toast.success('Análise pedida. O cliente recebe o pedido de autorização no app.')
          },
          onError: (erro) => {
            toast.error(errorMessage(erro))
          },
        })
      }}
    >
      {pedir.isPending ? <LoaderCircle className="animate-spin" /> : <ScanFace />}
      {deNovo ? 'Pedir a análise de novo' : 'Consultar no Mira Score'}
    </Button>
  )
}
